import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Trophy, Phone, Calendar, Building2, Award, TrendingUp, Crown, LineChart as LineIcon, Clock, Eye, Share2, Video } from "lucide-react";
import { EvolutionLine } from "@/components/Charts";
import { PendingRoomsBanner } from "@/components/PendingRoomsBanner";
import WeeklyClubMVPs from "@/components/WeeklyClubMVPs";
import DeadlineBanner from "@/components/DeadlineBanner";
import MeetingCountdown from "@/components/MeetingCountdown";
import ConvocationNotice from "@/components/ConvocationNotice";
import MeetingSummaryNotice from "@/components/MeetingSummaryNotice";
import StreakWidget from "@/components/StreakWidget";
import WeekFocusWidget from "@/components/gps/WeekFocusWidget";
import PerformanceGaps from "@/components/gps/PerformanceGaps";
import TeamLeadWidget from "@/components/TeamLeadWidget";
import FinancialPulseWidget from "@/components/gps/FinancialPulseWidget";
import ConversionSummary from "@/components/ConversionSummary";
import OnboardingWizard from "@/components/OnboardingWizard";
import ClubVoice from "@/components/ClubVoice";
import CoachChampions from "@/components/CoachChampions";
import DailyReportPopup from "@/components/DailyReportPopup";
import { useAuth } from "@/context/AuthContext";
import { usePhotoBlob } from "@/components/PhotoUpload";

const MVP_IMG = "https://images.unsplash.com/photo-1647580427155-0483906cb9de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxyZWFsJTIwZXN0YXRlJTIwYWdlbnQlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzgwNjYyNjY0fDA&ixlib=rb-4.1.0&q=85";

const KPIS = [
  { key: "total_contactos", label: "Contactos", icon: Phone },
  { key: "total_reunioes", label: "Reuniões", icon: Calendar },
  { key: "total_angariacoes", label: "Angariações", icon: Building2 },
  { key: "total_vendas", label: "Vendas", icon: TrendingUp },
  { key: "total_growth_shares", label: "Growth Shares", icon: Award },
  { key: "total_social_posts", label: "Posts sociais", icon: Share2 },
  { key: "total_humanized_videos", label: "Vídeos humanizados", icon: Video },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [evolution, setEvolution] = useState(null);
  const { user } = useAuth();
  const topEvolutionSeries = useMemo(() => {
    const series = evolution?.series || [];
    return [...series]
      .sort((a, b) => {
        const ta = a.weekly[a.weekly.length - 1]?.cumulative || 0;
        const tb = b.weekly[b.weekly.length - 1]?.cumulative || 0;
        return tb - ta;
      })
      .slice(0, 8);
  }, [evolution]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
    api.get("/evolution/players").then((r) => setEvolution(r.data)).catch(() => setEvolution({ series: [] }));
  }, []);

  const { settings = {}, kpis = {}, top_players = [], mvp, scope, scope_club } = data || {};
  const isCoachScope = scope === "coach_club";
  const inComp = settings.phase === "competition" && settings.competition_week > 0;
  const mvpPhoto = usePhotoBlob(mvp?.photo_url);

  return (
    <div className="space-y-10 kw-fade-up" data-testid="dashboard-page">
      <OnboardingWizard />
      <DailyReportPopup />
      <ConvocationNotice />
      <MeetingSummaryNotice />

      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kw-overline mb-2">
            {settings.edition_name || "KW Lead Growth League"}
            {isCoachScope && <span className="ml-2 text-[#EAB308]">· Vista do Secretário · Clube {scope_club}</span>}
          </div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            Dashboard <span className="text-[#E50000]">Executivo</span>
          </h1>
        </div>
        <div className="kw-card px-5 py-3" data-testid="dashboard-week-card">
          <div className="kw-overline">
            {settings.status === "upcoming" ? "Por arrancar" : `Fase · ${settings.phase_label || "—"}`}
          </div>
          <div className="font-display text-3xl font-black">
            {data ? (inComp ? settings.competition_week : (settings.current_week_computed || 0)) : "—"}
            <span className="text-[#737373]">/{inComp ? settings.competition_weeks : (settings.weeks_total || 13)}</span>
          </div>
          {inComp && (
            <div className="text-[10px] uppercase tracking-widest text-[#22C55E] mt-1">
              Semanas que pontuam
            </div>
          )}
          {settings.phase === "preparation" && (
            <div className="text-[10px] uppercase tracking-widest text-[#EAB308] mt-1">
              Coopetição arranca na semana {settings.competition_start_week}
            </div>
          )}
        </div>
      </div>

      {/* KPI grid */}
      <DeadlineBanner />

      {user?.role === "admin" && settings.status === "upcoming" &&
        (kpis.total_contactos > 0 || kpis.total_reunioes > 0 || kpis.competing_players > 0) && (
        <Link to="/settings" className="kw-card p-5 flex items-start gap-4 border-[#E50000] block kw-card-hover"
          data-testid="legacy-data-warning">
          <Trophy className="w-6 h-6 text-[#E50000] mt-1 shrink-0" />
          <div>
            <div className="font-display text-xl font-black uppercase tracking-tight text-[#E50000]">
              Há registos de uma edição anterior
            </div>
            <p className="text-sm text-[#A3A3A3] mt-1">
              A edição ainda não arrancou, mas existem {kpis.total_contactos} contactos, {kpis.total_reunioes} reuniões
              e {kpis.competing_players} a coopetir de dados antigos. Vai a <strong>Configuração → Recomeçar a edição</strong> para
              limpar e começar a zeros.
            </p>
          </div>
        </Link>
      )}

      {user?.role === "admin" && <PendingRoomsBanner />}

      <MeetingCountdown />

      <ClubVoice />
      <StreakWidget />
      {(user?.role === "consultant" || user?.role === "admin") && <WeekFocusWidget />}
      {(user?.role === "consultant" || user?.role === "admin") && <PerformanceGaps compact />}
      {(user?.role === "consultant" || user?.role === "admin") && <FinancialPulseWidget />}
      {(user?.role === "consultant" || user?.role === "admin") && <TeamLeadWidget />}

      <div className="space-y-3" data-testid="league-kpis-section">
        <div className="kw-overline text-[10px] text-[#737373]">Totais da liga · Edição actual · Todos os consultores</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4" data-testid="kpi-grid">
        {KPIS.map((k) => (
          <div key={k.key} className="kw-card p-5 kw-card-hover">
            <div className="flex items-center justify-between mb-3">
              <k.icon className="w-5 h-5 text-[#E50000]" />
              <div className="kw-overline">{k.label}</div>
            </div>
            <div className="font-display text-4xl font-black leading-none">{kpis[k.key] ?? 0}</div>
          </div>
        ))}
        </div>
      </div>

      <div>
        <div className="kw-overline mb-3">Plantel da edição</div>
        <div className="grid grid-cols-2 gap-3" data-testid="dashboard-roster-kpis">
          {[
            { key: "competing_players", label: "A coopetir", icon: Trophy, color: "#22C55E" },
            { key: "observer_players", label: "Observadores", icon: Eye, color: "#737373" },
          ].map((k) => (
            <Link key={k.key} to="/roster" className="kw-card p-5 kw-card-hover block" data-testid={`kpi-${k.key}`}>
              <div className="flex items-center justify-between mb-3">
                <k.icon className="w-5 h-5" style={{ color: k.color }} />
                <div className="kw-overline">{k.label}</div>
              </div>
              <div className="font-display text-4xl font-black leading-none" style={{ color: k.color }}>
                {kpis[k.key] ?? 0}
              </div>
            </Link>
          ))}
        </div>
        <p className="text-[10px] uppercase tracking-widest text-[#555] mt-2">
          Os observadores reportam e têm o seu Ranking Accountability, mas não contam para o campeonato
        </p>
      </div>

      <WeeklyClubMVPs />

      {(user?.role === "coach" || user?.role === "admin") && <CoachChampions />}

      <ConversionSummary highlightClub={user?.role === "coach" ? user?.club : null} />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Top players */}
        <div className="lg:col-span-2 kw-card p-6">
          <div className="flex items-center justify-between mb-6 border-b border-[#222] pb-4">
            <div>
              <div className="kw-overline">{isCoachScope ? `Ranking do clube ${scope_club}` : "Ranking principal"}</div>
              <h2 className="font-display text-2xl font-black uppercase">{isCoachScope ? "Top 3 do Clube" : "Top 5 Consultores"}</h2>
            </div>
            <Trophy className="w-6 h-6 text-[#E50000]" />
          </div>

          {top_players.length === 0 ? (
            <div className="text-[#737373] text-sm py-8 text-center" data-testid="top-players-empty">
              Ainda não há coopetidores selecionados. O ranking abre quando os 8 + 4 de cada clube
              forem escolhidos — até lá, segue o Ranking Accountability.
            </div>
          ) : (
            <div className="space-y-2" data-testid="top-players">
              {top_players.map((p, i) => (
                <div key={p.player_id} className="flex items-center gap-4 p-3 hover:bg-[#141414] transition-colors">
                  <div
                    className={`font-display text-3xl font-black w-10 text-center ${
                      i === 0 ? "text-[#E50000]" : i === 1 ? "text-white" : "text-[#737373]"
                    }`}
                  >
                    {String(p.rank).padStart(2, "0")}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-xs text-[#737373] font-mono">
                      {p.totals.vendas}V · {p.totals.angariacoes}A · {p.totals.reunioes}R · {p.totals.contactos}C
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl font-black">{Math.round(p.total_points)}</div>
                    <div className="kw-overline">pontos</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* MVP */}
        <div className="kw-card overflow-hidden relative" data-testid="mvp-card">
          <div
            className="h-48 relative"
            data-testid="mvp-photo"
            data-has-photo={mvpPhoto ? "true" : "false"}
            style={{
              backgroundImage: `linear-gradient(180deg, transparent 0%, rgba(10,10,10,0.95) 100%), url(${mvpPhoto || MVP_IMG})`,
              backgroundSize: "cover",
              backgroundPosition: mvpPhoto ? "center 20%" : "center",
            }}
          >
            <div className="absolute top-4 left-4 bg-[#E50000] text-white px-3 py-1 font-display font-black uppercase tracking-widest text-xs kw-pulse">
              MVP
            </div>
            <Crown className="absolute top-4 right-4 w-7 h-7 text-[#E50000] drop-shadow-[0_0_10px_rgba(229,0,0,0.8)]" />
          </div>
          <div className="p-6 -mt-12 relative">
            <div className="kw-overline mb-1">Most Valuable Player</div>
            {mvp ? (
              <>
                <div className="font-display text-3xl font-black uppercase tracking-tight">{mvp.name}</div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div className="border border-[#222] p-2">
                    <div className="font-display text-2xl font-black text-[#E50000]">{mvp.totals.vendas}</div>
                    <div className="kw-overline">Vendas</div>
                  </div>
                  <div className="border border-[#222] p-2">
                    <div className="font-display text-2xl font-black">{Math.round(mvp.total_points)}</div>
                    <div className="kw-overline">Pontos</div>
                  </div>
                  <div className="border border-[#222] p-2">
                    <div className="font-display text-2xl font-black">{mvp.completo_count}</div>
                    <div className="kw-overline">Reports</div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-[#737373] text-sm pt-2">Ainda não há MVP atribuído.</div>
            )}
          </div>
        </div>
      </div>

      {/* Evolution chart */}
      <div className="kw-card p-6">
        <div className="flex items-center justify-between mb-6 border-b border-[#222] pb-4">
          <div>
            <div className="kw-overline">Tendência</div>
            <h2 className="font-display text-2xl font-black uppercase">Evolução Semanal · Top {Math.min(8, evolution?.series?.length || 0)}</h2>
          </div>
          <LineIcon className="w-6 h-6 text-[#E50000]" />
        </div>
        {topEvolutionSeries.length === 0 ? (
          <div className="text-[#737373] text-sm py-12 text-center" data-testid="evolution-empty">
            A evolução começa a desenhar-se quando os coopetidores estiverem selecionados e a reportar.
          </div>
        ) : (
          <EvolutionLine series={topEvolutionSeries} dataKey="cumulative" height={320} />
        )}
      </div>
    </div>
  );
}
